import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
    Button,
    CustomInput,
    Form,
    FormGroup,
    Input,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';

import Widget from '../../../components/Widget/Widget';
import {
    loadSystemSettingsRequest,
    reconcileWorkPeriods,
    saveSystemSettingsRequest,
    loadMerchantNotificationSettingsRequest,
    saveMerchantNotificationSettingsRequest,
    resetStationNotificationSettingsRequest,
} from '../../../actions/settings';
import { withTranslation } from '../../../i18n/LanguageContext';
import LanguageSelect from '../../../i18n/LanguageSelect';

import s from './SystemSettings.module.scss';

const EMPTY_SETTINGS = {
    autoSendEmployeeShiftReportSms: false,
    autoSendEmployeeShiftReportEmail: false,
    autoSendEmployeePayoutSms: false,
    pooledTips: false,
    displayLanguage: 'en',
    smsDeliveryConfigured: false,
    emailDeliveryConfigured: false,
};

const EMPTY_NOTIFICATION_DEFAULTS = {
    enableQuickServeFlowDefault: false,
    autoNotifySmsOnOrderCompleteDefault: false,
    autoNotifyEmailOnOrderCompleteDefault: false,
    defaultOrderCompleteMessage: 'Your order is ready',
};

class SystemSettings extends React.Component {
    static propTypes = {
        dispatch: PropTypes.func.isRequired,
    };

    state = {
        settings: EMPTY_SETTINGS,
        draft: EMPTY_SETTINGS,
        loading: true,
        saving: false,
        loadError: '',
        notificationDefaults: EMPTY_NOTIFICATION_DEFAULTS,
        notificationDraft: EMPTY_NOTIFICATION_DEFAULTS,
        notificationLoading: true,
        notificationLoadError: '',
        notificationSaving: false,
        isReconcileOpen: false,
        isReconciling: false,
        isResetStationsOpen: false,
        isResettingStations: false,
    };

    componentDidMount() {
        this.loadSettings();
        this.loadNotificationDefaults();
    }

    loadSettings = async () => {
        this.setState({ loading: true, loadError: '' });
        try {
            const settings = await this.props.dispatch(loadSystemSettingsRequest());
            this.setState({
                settings,
                draft: settings,
                loading: false,
            });
        } catch (err) {
            this.setState({
                loading: false,
                loadError: err?.message || this.props.t('system.loadFailed'),
            });
        }
    };

    loadNotificationDefaults = async () => {
        this.setState({ notificationLoading: true, notificationLoadError: '' });
        try {
            const notificationDefaults = await this.props.dispatch(loadMerchantNotificationSettingsRequest());
            this.setState({
                notificationDefaults,
                notificationDraft: notificationDefaults,
                notificationLoading: false,
            });
        } catch (err) {
            this.setState({
                notificationLoading: false,
                notificationLoadError: err?.message || this.props.t('system.notificationLoadFailed'),
            });
        }
    };

    handleToggle = (field) => (e) => {
        const checked = e.target.checked;
        this.setState((prev) => ({
            draft: {
                ...prev.draft,
                [field]: checked,
            },
        }));
    };

    handlePosLanguageChange = (code) => {
        this.setState((prev) => ({
            draft: {
                ...prev.draft,
                displayLanguage: code,
            },
        }));
    };

    handleSavePosLanguage = async (e) => {
        e.preventDefault();
        const { draft } = this.state;
        this.setState({ saving: true });
        try {
            const settings = await this.props.dispatch(saveSystemSettingsRequest({
                displayLanguage: draft.displayLanguage,
            }));
            this.setState({
                settings,
                draft: settings,
            });
        } catch (err) {
            // toast shown in action
        } finally {
            this.setState({ saving: false });
        }
    };

    handleNotificationToggle = (field) => (e) => {
        const checked = e.target.checked;
        this.setState((prev) => ({
            notificationDraft: {
                ...prev.notificationDraft,
                [field]: checked,
            },
        }));
    };

    handleNotificationMessageChange = (e) => {
        this.setState((prev) => ({
            notificationDraft: {
                ...prev.notificationDraft,
                defaultOrderCompleteMessage: e.target.value,
            },
        }));
    };

    handleSave = async (e) => {
        e.preventDefault();
        const { draft } = this.state;
        this.setState({ saving: true });
        try {
            const settings = await this.props.dispatch(saveSystemSettingsRequest({
                autoSendEmployeeShiftReportSms: draft.autoSendEmployeeShiftReportSms,
                autoSendEmployeeShiftReportEmail: draft.autoSendEmployeeShiftReportEmail,
            }));
            this.setState({
                settings,
                draft: settings,
            });
        } catch (err) {
            // toast shown in action
        } finally {
            this.setState({ saving: false });
        }
    };

    handleSavePayoutSms = async (e) => {
        e.preventDefault();
        const { draft } = this.state;
        this.setState({ saving: true });
        try {
            const settings = await this.props.dispatch(saveSystemSettingsRequest({
                autoSendEmployeePayoutSms: !!draft.autoSendEmployeePayoutSms,
            }));
            this.setState({
                settings,
                draft: settings,
            });
        } catch (err) {
            // toast shown in action
        } finally {
            this.setState({ saving: false });
        }
    };

    handleSavePooledTips = async (e) => {
        e.preventDefault();
        const { draft } = this.state;
        this.setState({ saving: true });
        try {
            const settings = await this.props.dispatch(saveSystemSettingsRequest({
                pooledTips: !!draft.pooledTips,
            }));
            this.setState({
                settings,
                draft: settings,
            });
        } catch (err) {
            // toast shown in action
        } finally {
            this.setState({ saving: false });
        }
    };

    handleSaveNotificationDefaults = async (e) => {
        e.preventDefault();
        const { notificationDraft } = this.state;
        this.setState({ notificationSaving: true });
        try {
            const notificationDefaults = await this.props.dispatch(saveMerchantNotificationSettingsRequest({
                enableQuickServeFlowDefault: notificationDraft.enableQuickServeFlowDefault,
                autoNotifySmsOnOrderCompleteDefault: notificationDraft.autoNotifySmsOnOrderCompleteDefault,
                autoNotifyEmailOnOrderCompleteDefault: notificationDraft.autoNotifyEmailOnOrderCompleteDefault,
                defaultOrderCompleteMessage: notificationDraft.defaultOrderCompleteMessage,
            }));
            this.setState({
                notificationDefaults,
                notificationDraft: notificationDefaults,
            });
        } catch (err) {
            // toast shown in action
        } finally {
            this.setState({ notificationSaving: false });
        }
    };

    openReconcileConfirm = (e) => {
        e.preventDefault();
        this.setState({ isReconcileOpen: true });
    };

    closeReconcileConfirm = () => {
        if (this.state.isReconciling) return;
        this.setState({ isReconcileOpen: false });
    };

    onConfirmReconcile = async () => {
        this.setState({ isReconciling: true });
        try {
            await this.props.dispatch(reconcileWorkPeriods());
            this.setState({ isReconcileOpen: false });
        } catch (err) {
            // toast shown in action
        } finally {
            this.setState({ isReconciling: false });
        }
    };

    openResetStationsConfirm = (e) => {
        e.preventDefault();
        this.setState({ isResetStationsOpen: true });
    };

    closeResetStationsConfirm = () => {
        if (this.state.isResettingStations) return;
        this.setState({ isResetStationsOpen: false });
    };

    onConfirmResetStations = async () => {
        this.setState({ isResettingStations: true });
        try {
            await this.props.dispatch(resetStationNotificationSettingsRequest());
            this.setState({ isResetStationsOpen: false });
        } catch (err) {
            // toast shown in action
        } finally {
            this.setState({ isResettingStations: false });
        }
    };

    renderDeliveryNote = (channel, configured) => {
        if (configured) {
            return null;
        }

        const label = channel === 'sms' ? 'SMS' : 'Email';
        return (
            <p className={s.infoNote}>
                {this.props.t('system.deliveryNotConfigured', { channel: label })}
            </p>
        );
    };

    render() {
        const {
            draft,
            loading,
            saving,
            loadError,
            notificationDefaults,
            notificationDraft,
            notificationLoading,
            notificationLoadError,
            notificationSaving,
            isReconcileOpen,
            isReconciling,
            isResetStationsOpen,
            isResettingStations,
        } = this.state;
        const { t } = this.props;

        const hasChanges =
            draft.autoSendEmployeeShiftReportSms !== this.state.settings.autoSendEmployeeShiftReportSms
            || draft.autoSendEmployeeShiftReportEmail !== this.state.settings.autoSendEmployeeShiftReportEmail;

        const hasPooledTipsChange = draft.pooledTips !== this.state.settings.pooledTips;
        const hasPayoutSmsChange = !!draft.autoSendEmployeePayoutSms !== !!this.state.settings.autoSendEmployeePayoutSms;
        const hasPosLanguageChange = draft.displayLanguage !== this.state.settings.displayLanguage;

        const hasNotificationChanges =
            notificationDraft.enableQuickServeFlowDefault !== notificationDefaults.enableQuickServeFlowDefault
            || notificationDraft.autoNotifySmsOnOrderCompleteDefault !== notificationDefaults.autoNotifySmsOnOrderCompleteDefault
            || notificationDraft.autoNotifyEmailOnOrderCompleteDefault !== notificationDefaults.autoNotifyEmailOnOrderCompleteDefault
            || notificationDraft.defaultOrderCompleteMessage !== notificationDefaults.defaultOrderCompleteMessage;

        return (
            <div className={s.page}>
                <Widget className={s.systemWidget}>
                    <div className={s.cards}>
                    <div className={s.cardsTwoCol}>
                    <div className={s.sectionCard}>
                        <div className={s.sectionCardHeader}>{t('language.heading')}</div>
                        <div className={s.sectionCardBody}>
                            <p className={s.intro}>{t('language.help')}</p>
                            <LanguageSelect id="settings-display-language" variant="settings" />
                        </div>
                    </div>
                    <div className={s.sectionCard}>
                        <div className={s.sectionCardHeader}>{t('language.posHeading')}</div>
                        <div className={s.sectionCardBody}>
                            <p className={s.intro}>{t('language.posHelp')}</p>
                            {!loading && !loadError ? (
                                <Form onSubmit={this.handleSavePosLanguage}>
                                    <div className={s.posLanguageRow}>
                                        <LanguageSelect
                                            id="settings-pos-display-language"
                                            variant="settings"
                                            labelKey="language.posLabel"
                                            value={draft.displayLanguage || 'en'}
                                            onChange={this.handlePosLanguageChange}
                                        />
                                        <button
                                            type="submit"
                                            className={`${s.primaryBtn} ${s.posSaveBtn}`}
                                            disabled={saving || !hasPosLanguageChange}
                                        >
                                            {saving ? t('common.saving') : t('language.posSave')}
                                        </button>
                                    </div>
                                </Form>
                            ) : null}
                        </div>
                    </div>
                    </div>
                    <div className={s.sectionCard}>
                        <div className={s.sectionCardHeader}>{t('system.deliveryHeading')}</div>
                        <div className={s.sectionCardBody}>
                            <p className={s.intro}>{t('system.deliveryIntro')}</p>
                            {loadError ? <p className={s.errorText}>{loadError}</p> : null}
                            {loading && !loadError ? (
                                <p className={s.noteText}>{t('system.loadingSettings')}</p>
                            ) : null}
                            {!loading && !loadError ? (
                                <Form onSubmit={this.handleSave}>
                                    <div className={s.toggleGroup}>
                                        <div className={s.toggleTwoCol}>
                                            <div className={s.toggleItem}>
                                                <FormGroup check className={s.toggleRow}>
                                                    <CustomInput
                                                        type="switch"
                                                        id="autoSendEmployeeShiftReportSms"
                                                        label={t('system.smsToggle')}
                                                        checked={draft.autoSendEmployeeShiftReportSms}
                                                        onChange={this.handleToggle('autoSendEmployeeShiftReportSms')}
                                                        disabled={saving}
                                                    />
                                                </FormGroup>
                                                <p className={s.helpText}>
                                                    {t('system.smsHelp')}
                                                </p>
                                                {this.renderDeliveryNote('sms', draft.smsDeliveryConfigured)}
                                            </div>

                                            <div className={s.toggleItem}>
                                                <FormGroup check className={s.toggleRow}>
                                                    <CustomInput
                                                        type="switch"
                                                        id="autoSendEmployeeShiftReportEmail"
                                                        label={t('system.emailToggle')}
                                                        checked={draft.autoSendEmployeeShiftReportEmail}
                                                        onChange={this.handleToggle('autoSendEmployeeShiftReportEmail')}
                                                        disabled={saving}
                                                    />
                                                </FormGroup>
                                                <p className={s.helpText}>
                                                    {t('system.emailHelp')}
                                                </p>
                                                {this.renderDeliveryNote('email', draft.emailDeliveryConfigured)}
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        className={`${s.primaryBtn} ${s.saveBtn}`}
                                        disabled={saving || !hasChanges}
                                    >
                                        {saving ? t('common.saving') : t('system.saveDelivery')}
                                    </button>
                                </Form>
                            ) : null}
                        </div>
                    </div>

                    <div className={s.sectionCard}>
                        <div className={s.sectionCardHeader}>{t('system.pooledHeading')}</div>
                        <div className={s.sectionCardBody}>
                            <p className={s.intro}>{t('system.pooledIntro')}</p>
                            {!loading && !loadError ? (
                                <Form onSubmit={this.handleSavePooledTips}>
                                    <div className={s.toggleGroup}>
                                        <div className={s.toggleItem}>
                                            <FormGroup check className={s.toggleRow}>
                                                <CustomInput
                                                    type="switch"
                                                    id="pooledTips"
                                                    label={t('system.pooledToggle')}
                                                    checked={!!draft.pooledTips}
                                                    onChange={this.handleToggle('pooledTips')}
                                                    disabled={saving}
                                                />
                                            </FormGroup>
                                            <p className={s.helpText}>
                                                {t('system.pooledHelp')}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        className={`${s.primaryBtn} ${s.saveBtn}`}
                                        disabled={saving || !hasPooledTipsChange}
                                    >
                                        {saving ? t('common.saving') : t('system.savePooled')}
                                    </button>
                                </Form>
                            ) : null}
                        </div>
                    </div>

                    <div className={s.sectionCard}>
                        <div className={s.sectionCardHeader}>{t('system.notificationsHeading')}</div>
                        <div className={s.sectionCardBody}>
                            <p className={s.intro}>{t('system.notificationsIntro')}</p>
                            {notificationLoadError ? <p className={s.errorText}>{notificationLoadError}</p> : null}
                            {notificationLoading && !notificationLoadError ? (
                                <p className={s.noteText}>{t('system.loadingNotifications')}</p>
                            ) : null}
                            {!notificationLoading && !notificationLoadError ? (
                                <Form onSubmit={this.handleSaveNotificationDefaults}>
                                    <div className={s.toggleGroup}>
                                        <div className={s.toggleItem}>
                                            <FormGroup check className={s.toggleRow}>
                                                <CustomInput
                                                    type="switch"
                                                    id="enableQuickServeFlowDefault"
                                                        label={t('system.quickServeToggle')}
                                                    checked={notificationDraft.enableQuickServeFlowDefault}
                                                    onChange={this.handleNotificationToggle('enableQuickServeFlowDefault')}
                                                    disabled={notificationSaving}
                                                />
                                            </FormGroup>
                                            <p className={s.helpText}>
                                                {t('system.quickServeHelp')}
                                            </p>
                                        </div>

                                        <div className={s.toggleTwoCol}>
                                            <div className={s.toggleItem}>
                                                <FormGroup check className={s.toggleRow}>
                                                    <CustomInput
                                                        type="switch"
                                                        id="autoNotifySmsOnOrderCompleteDefault"
                                                        label={t('system.autoSmsToggle')}
                                                        checked={notificationDraft.autoNotifySmsOnOrderCompleteDefault}
                                                        onChange={this.handleNotificationToggle('autoNotifySmsOnOrderCompleteDefault')}
                                                        disabled={notificationSaving}
                                                    />
                                                </FormGroup>
                                                <p className={s.helpText}>
                                                    {t('system.autoSmsHelp')}
                                                </p>
                                            </div>

                                            <div className={s.toggleItem}>
                                                <FormGroup check className={s.toggleRow}>
                                                    <CustomInput
                                                        type="switch"
                                                        id="autoNotifyEmailOnOrderCompleteDefault"
                                                        label={t('system.autoEmailToggle')}
                                                        checked={notificationDraft.autoNotifyEmailOnOrderCompleteDefault}
                                                        onChange={this.handleNotificationToggle('autoNotifyEmailOnOrderCompleteDefault')}
                                                        disabled={notificationSaving}
                                                    />
                                                </FormGroup>
                                                <p className={s.helpText}>
                                                    {t('system.autoEmailHelp')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <FormGroup className={s.messageField}>
                                        <label htmlFor="defaultOrderCompleteMessage" className={s.messageLabel}>
                                            {t('system.defaultMessageLabel')}
                                        </label>
                                        <Input
                                            type="textarea"
                                            id="defaultOrderCompleteMessage"
                                            rows={3}
                                            value={notificationDraft.defaultOrderCompleteMessage}
                                            onChange={this.handleNotificationMessageChange}
                                            disabled={notificationSaving}
                                            className={s.messageInput}
                                        />
                                        <p className={s.helpText}>
                                            {t('system.defaultMessageHelp')}
                                        </p>
                                    </FormGroup>

                                    <div className={s.actionRow}>
                                        <button
                                            type="submit"
                                            className={`${s.primaryBtn} ${s.saveBtn}`}
                                            disabled={notificationSaving || !hasNotificationChanges}
                                        >
                                            {notificationSaving ? t('common.saving') : t('system.saveNotifications')}
                                        </button>
                                        <button
                                            type="button"
                                            className={s.secondaryBtn}
                                            onClick={this.openResetStationsConfirm}
                                            disabled={notificationSaving || isResettingStations}
                                        >
                                            {t('system.resetStations')}
                                        </button>
                                    </div>
                                </Form>
                            ) : null}
                        </div>
                    </div>

                    <div className={s.sectionCard}>
                        <div className={s.sectionCardHeader}>{t('system.payoutSmsHeading')}</div>
                        <div className={s.sectionCardBody}>
                            <p className={s.intro}>{t('system.payoutSmsIntro')}</p>
                            {!loading && !loadError ? (
                                <Form onSubmit={this.handleSavePayoutSms}>
                                    <div className={s.toggleGroup}>
                                        <div className={s.toggleItem}>
                                            <FormGroup check className={s.toggleRow}>
                                                <CustomInput
                                                    type="switch"
                                                    id="autoSendEmployeePayoutSms"
                                                    label={t('system.payoutSmsToggle')}
                                                    checked={!!draft.autoSendEmployeePayoutSms}
                                                    onChange={this.handleToggle('autoSendEmployeePayoutSms')}
                                                    disabled={saving}
                                                />
                                            </FormGroup>
                                            <p className={s.helpText}>
                                                {t('system.payoutSmsHelp')}
                                            </p>
                                            {this.renderDeliveryNote('sms', draft.smsDeliveryConfigured)}
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        className={`${s.primaryBtn} ${s.saveBtn}`}
                                        disabled={saving || !hasPayoutSmsChange}
                                    >
                                        {saving ? t('common.saving') : t('system.savePayoutSms')}
                                    </button>
                                </Form>
                            ) : null}
                        </div>
                    </div>

                    <div className={s.sectionCard}>
                        <div className={s.sectionCardHeader}>{t('system.workPeriodHeading')}</div>
                        <div className={s.sectionCardBody}>
                            <p className={s.intro}>{t('system.workPeriodIntro')}</p>
                            <p className={s.helpText}>{t('system.workPeriodHelp')}</p>
                            <button
                                type="button"
                                className={s.primaryBtn}
                                onClick={this.openReconcileConfirm}
                                disabled={isReconciling}
                            >
                                {t('system.reconcile')}
                            </button>
                        </div>
                    </div>
                    </div>
                </Widget>

                <Modal
                    isOpen={isReconcileOpen}
                    toggle={this.closeReconcileConfirm}
                    backdrop={isReconciling ? 'static' : true}
                    className={s.darkModal}
                >
                    <ModalHeader toggle={isReconciling ? undefined : this.closeReconcileConfirm}>
                        {t('system.reconcileTitle')}
                    </ModalHeader>
                    <ModalBody>
                        <p>{t('system.reconcileBody')}</p>
                        <p className={s.helpText} style={{ marginBottom: 0 }}>
                            {t('system.reconcileHelp')}
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={this.closeReconcileConfirm} disabled={isReconciling}>
                            {t('common.cancel')}
                        </Button>
                        <Button color="primary" onClick={this.onConfirmReconcile} disabled={isReconciling}>
                            {isReconciling ? t('system.reconciling') : t('system.reconcile')}
                        </Button>
                    </ModalFooter>
                </Modal>

                <Modal
                    isOpen={isResetStationsOpen}
                    toggle={this.closeResetStationsConfirm}
                    backdrop={isResettingStations ? 'static' : true}
                    className={s.darkModal}
                >
                    <ModalHeader toggle={isResettingStations ? undefined : this.closeResetStationsConfirm}>
                        {t('system.resetTitle')}
                    </ModalHeader>
                    <ModalBody>
                        <p>{t('system.resetBody')}</p>
                        <p className={s.helpText} style={{ marginBottom: 0 }}>
                            {t('system.resetHelp')}
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={this.closeResetStationsConfirm} disabled={isResettingStations}>
                            {t('common.cancel')}
                        </Button>
                        <Button color="primary" onClick={this.onConfirmResetStations} disabled={isResettingStations}>
                            {isResettingStations ? t('system.resetting') : t('system.resetConfirm')}
                        </Button>
                    </ModalFooter>
                </Modal>
            </div>
        );
    }
}

export default connect()(withTranslation(SystemSettings));
